import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import GarageIcon from '@mui/icons-material/Garage';
import { useNavigate } from 'react-router-dom';
import {
  TimelineCalendar,
  TimelineItem,
  TimelineRow,
  TimelineRowGroup,
} from 'mq-timeline-calendar/react';
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addWeeks,
  addMonths,
  subDays,
  subWeeks,
  subMonths,
  setHours,
  format,
} from 'date-fns';
import {
  useAllLifts,
  getMaintenanceStatusColor,
  type LiftType,
} from '@/hooks';
import { useCalendarBookings, type CalendarBooking } from '@/hooks/useBookings';
import { useUIStore } from '@/stores/uiStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useTenantSettings, toAvailabilityWeekly } from '@/hooks/useTenantSettings';
import {
  getBusinessHoursRange,
  hasWeekendHours,
  stepToOpenDay,
  buildHoursExceptionIndex,
  applyRangeOverridesToWeekly,
  buildOpenRangeTimestamps,
  ALL_WEEKDAYS_CLOSED,
} from '@/lib/inventoryUtils';
import { useHoursExceptions } from '@/hooks/useHoursExceptions';
import { useCalendarStore } from '@/stores/calendarStore';
import { useSwipe } from '@/hooks/useSwipe';
import { BookingItem } from '@/components/calendar/BookingItem';
import { EmptyState } from '@/components/common/EmptyState';
import { calendarEmptyReason } from './calendarEmptyReason';
import { resolveBookingOccupancyRange } from './calendarSegments';
import type { Booking } from '@/types';
import { calendarLocaleFor } from './supervisorTaskCalendar';
import { currentLanguage, dateFnsLocale } from '@/utils/locale';

type CalendarView = 'day' | 'week' | 'month';

// Exported for tests: NULL-palvelu kaatoi tämän muunnoksen (#2455).
export function mapToBooking(cb: CalendarBooking): Booking {
  // One lift block per booking. Occupancy follows per-service planned hours
  // (sequential sum / parallel max), same as the work-order calendar (#2868).
  const { startMs, endMs, hours } = resolveBookingOccupancyRange(cb);

  const booking: Booking = {
    id: cb.id,
    customerId: cb.customerId,
    customerName: cb.customerName,
    customerPhone: '',
    vehicleId: cb.vehicleId,
    vehicleRegNumber: cb.vehicleRegNumber,
    vehicleMakeModel: cb.vehicleMakeModel,
    mechanicId: cb.assignedMechanicId ?? '',
    mechanicName: cb.mechanicName ?? '',
    scheduledStart: new Date(startMs).toISOString(),
    scheduledEnd: new Date(endMs).toISOString(),
    status: cb.status,
    services: (cb.serviceType ?? '')
      .split(', ')
      .filter(Boolean)
      .map((name, i) => ({
        id: `svc-${String(i)}`,
        name,
        estimatedHours: i === 0 ? hours : 0,
        priceCents: 0,
      })),
    estimatedTotalCents: 0,
    assignedMechanicIds: [],
    hasUnassignedServices: false,
  };
  // Derived approval wait (#3027) rides along; the block keeps its own status.
  if (cb.isWaitingForApproval === true) {
    booking.isWaitingForApproval = true;
    if (cb.openApprovalCount !== undefined) booking.openApprovalCount = cb.openApprovalCount;
  }
  return booking;
}

interface LiftCalendarTabProps {
  /** Fill the parent's available height (desktop full-bleed layout). */
  fillHeight?: boolean;
}

export function LiftCalendarTab({ fillHeight = false }: LiftCalendarTabProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('equipment');
  const { t: tc } = useTranslation('common');
  const { t: tcal } = useTranslation('calendar');
  // Shared with the work-order calendar so the two axes agree (#1313).
  const compressClosedHours = useCalendarStore((s) => s.compressClosedHours);
  const setCompressClosedHours = useCalendarStore((s) => s.setCompressClosedHours);
  const themeMode = useUIStore((s) => s.themeMode);
  const isMobile = useIsMobile();
  const fill = fillHeight && !isMobile;
  const calendarHeight = fill
    ? '100%'
    : isMobile
      ? 'calc(100vh - 290px)'
      : 'calc(100vh - 370px)';
  const { calendarHours } = useTenantSettings();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CalendarView>(() => {
    if (isMobile) return 'day';
    const saved = localStorage.getItem('equipmentCalendarView') as CalendarView | null;
    return saved ?? 'week';
  });
  const [typeFilter, setTypeFilter] = useState<LiftType | ''>('');

  const handleViewChange = (v: CalendarView) => {
    setView(v);
    localStorage.setItem('equipmentCalendarView', v);
  };

  useEffect(() => {
    if (isMobile && view === 'week') {
      setView('day');
    }
  }, [isMobile, view]);

  const businessRange = useMemo(() => getBusinessHoursRange(calendarHours), [calendarHours]);
  const showWeekend = useMemo(() => hasWeekendHours(calendarHours), [calendarHours]);

  // Opening hours re-keyed to the weekday convention the calendar library
  // reads (`Date.getDay()`), which is not the ISO one CalendarHours uses.
  // Per-date overrides (#1282). Folded into the weekday key over the visible
  // window, which is exact for day and week views — each names a weekday at most
  // once. A month window falls back to the plain weekly pattern (#1302).
  const { exceptions: hoursExceptions } = useHoursExceptions();
  const exceptionIndex = useMemo(
    () => buildHoursExceptionIndex(hoursExceptions),
    [hoursExceptions],
  );

  const { startDate, endDate } = useMemo((): { startDate: Date; endDate: Date } => {
    if (isMobile) {
      switch (view) {
        case 'day':
          return {
            startDate: setHours(startOfDay(currentDate), businessRange.startHour),
            endDate: setHours(startOfDay(currentDate), businessRange.endHour),
          };
        case 'week':
          return { startDate: subDays(currentDate, 3), endDate: addDays(currentDate, 3) };
        case 'month':
          return { startDate: subDays(currentDate, 15), endDate: addDays(currentDate, 15) };
      }
    }
    switch (view) {
      case 'day':
        return {
          startDate: setHours(startOfDay(currentDate), businessRange.startHour),
          endDate: setHours(startOfDay(currentDate), businessRange.endHour),
        };
      case 'week': {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return {
          startDate: weekStart,
          endDate: showWeekend
            ? endOfWeek(currentDate, { weekStartsOn: 1 })
            : endOfDay(addDays(weekStart, 4)),
        };
      }
      case 'month':
        return { startDate: startOfMonth(currentDate), endDate: endOfMonth(currentDate) };
    }
  }, [currentDate, view, isMobile, businessRange, showWeekend]);

  const rangeAvailabilityWeekly = useMemo(
    () => toAvailabilityWeekly(
      applyRangeOverridesToWeekly(startDate, endDate, calendarHours, exceptionIndex),
    ),
    [startDate, endDate, calendarHours, exceptionIndex]
  );

  // Windows longer than a week state their opening hours as absolute spans; a
  // weekday key cannot name one closed date there (#1302).
  const timelineAvailability = useMemo(() => {
    const windowDays = (endDate.getTime() - startDate.getTime()) / 86_400_000;
    const spans =
      exceptionIndex.size > 0 && windowDays > 7
        ? buildOpenRangeTimestamps(startDate, endDate, calendarHours, exceptionIndex)
        : [];
    return spans.length > 0
      ? { specific: spans, weekly: ALL_WEEKDAYS_CLOSED }
      : { weekly: rangeAvailabilityWeekly };
  }, [startDate, endDate, calendarHours, exceptionIndex, rangeAvailabilityWeekly]);

  // Query range with padding
  const queryRange = useMemo((): { start: string; end: string } => ({
    start: subDays(startDate, 14).toISOString(),
    end: addDays(endDate, 14).toISOString(),
  }), [startDate, endDate]);

  const { data: liftsData, isLoading: liftsLoading } = useAllLifts(true);
  const { data: calendarData, isLoading: bookingsLoading } = useCalendarBookings(
    queryRange.start,
    queryRange.end
  );

  const lifts = useMemo(() => {
    if (!liftsData) return [];
    if (!typeFilter) return liftsData;
    return liftsData.filter((l) => l.type === typeFilter);
  }, [liftsData, typeFilter]);

  const bookings = useMemo(() => {
    if (!calendarData?.data) return [];
    return calendarData.data.map(mapToBooking);
  }, [calendarData]);

  // Group bookings by assigned lift
  const bookingsByLift = useMemo(() => {
    const map = new Map<string, Booking[]>();
    if (!calendarData?.data) return map;
    for (const cb of calendarData.data) {
      if (cb.assignedLiftId) {
        const booking = bookings.find((b) => b.id === cb.id);
        if (booking) {
          const arr = map.get(cb.assignedLiftId) ?? [];
          arr.push(booking);
          map.set(cb.assignedLiftId, arr);
        }
      }
    }
    return map;
  }, [bookings, calendarData]);

  // Navigation — day view skips days the workshop is closed (#1276)
  const handlePrevious = useCallback(() => {
    switch (view) {
      case 'day': setCurrentDate((d) => stepToOpenDay(d, -1, calendarHours, exceptionIndex)); break;
      case 'week': setCurrentDate((d) => subWeeks(d, 1)); break;
      case 'month': setCurrentDate((d) => subMonths(d, 1)); break;
    }
  }, [view, calendarHours, exceptionIndex]);

  const handleNext = useCallback(() => {
    switch (view) {
      case 'day': setCurrentDate((d) => stepToOpenDay(d, 1, calendarHours, exceptionIndex)); break;
      case 'week': setCurrentDate((d) => addWeeks(d, 1)); break;
      case 'month': setCurrentDate((d) => addMonths(d, 1)); break;
    }
  }, [view, calendarHours, exceptionIndex]);

  const handleToday = useCallback(() => { setCurrentDate(new Date()); }, []);

  const handleSwipe = useCallback((deltaX: number) => {
    if (deltaX < 0) handleNext(); else handlePrevious();
  }, [handleNext, handlePrevious]);

  const swipeHandlers = useSwipe({ onSwipe: handleSwipe, threshold: 50 });

  const isLoading = liftsLoading || bookingsLoading;

  const emptyReason = calendarEmptyReason({
    isLoading,
    liftCount: lifts.length,
    bookingCount: bookings.length,
  });

  if (isLoading && !calendarData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} role="status" aria-label={tc('loading')}>
        <CircularProgress />
      </Box>
    );
  }

  const dateLabel = (() => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE d.M.yyyy', { locale: dateFnsLocale() });
      case 'week': {
        const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
        const we = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(ws, 'd.M.')} – ${format(we, 'd.M.yyyy')}`;
      }
      case 'month':
        return format(currentDate, 'LLLL yyyy', { locale: dateFnsLocale() });
    }
  })();

  return (
    <Box
      className="os-timeline-root"
      sx={
        fill
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      {/* Toolbar */}
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexShrink: 0 }} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={tc('previous')}>
            <IconButton onClick={handlePrevious} size="small" aria-label={tc('previous')}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('today')}>
            <IconButton onClick={handleToday} size="small" aria-label={tc('today')}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('next')}>
            <IconButton onClick={handleNext} size="small" aria-label={tc('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant="h6" sx={{ minWidth: 200, textTransform: 'capitalize' }}>
          {dateLabel}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ ml: 'auto !important' }}>
          <Chip
            label={tcal('compressClosedHours')}
            title={tcal('compressClosedHoursHint')}
            onClick={() => { setCompressClosedHours(!compressClosedHours); }}
            color={compressClosedHours ? 'primary' : 'default'}
            variant={compressClosedHours ? 'filled' : 'outlined'}
            size="small"
          />
          {(['day', 'week', 'month'] as CalendarView[]).map((v) => (
            <Chip
              key={v}
              label={tc(v)}
              onClick={() => { handleViewChange(v); }}
              color={view === v ? 'primary' : 'default'}
              variant={view === v ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t('calendar.filterByType')}</InputLabel>
          <Select
            value={typeFilter}
            label={t('calendar.filterByType')}
            onChange={(e) => { setTypeFilter(e.target.value as LiftType | ''); }}
          >
            <MenuItem value="">{t('calendar.allLifts')}</MenuItem>
            <MenuItem value="2_post">{t('lifts.type2Post')}</MenuItem>
            <MenuItem value="4_post">{t('lifts.type4Post')}</MenuItem>
            <MenuItem value="scissor">{t('lifts.typeScissor')}</MenuItem>
            <MenuItem value="pit">{t('lifts.typePit')}</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Calendar */}
      <Paper
        elevation={0}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          touchAction: isMobile ? 'pan-y' : 'auto',
          ...(fill && { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
        }}
        {...(isMobile ? swipeHandlers : {})}
      >
        <TimelineCalendar
          startDate={startDate}
          endDate={endDate}
          height={calendarHeight}
          showCurrentTime
          locale={calendarLocaleFor(currentLanguage())}
          theme={themeMode}
          compressClosedHours={
            compressClosedHours ? { availability: { weekly: rangeAvailabilityWeekly } } : false
          }
          availability={{
            ...timelineAvailability,
            unavailableStyle: {
              backgroundColor: themeMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            },
          }}
          sidebar={!isMobile && lifts.length > 1 ? {
            width: 160,
            content: (
              <>
                {lifts.map((lift) => (
                  <Box
                    key={lift.id}
                    sx={{
                      height: 'var(--timeline-row-height, 60px)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      borderBottom: 1,
                      borderColor: 'divider',
                      boxSizing: 'border-box',
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: `${getMaintenanceStatusColor(lift.maintenanceStatus)}.main`,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" fontWeight={600} noWrap lineHeight={1.2}>
                        {lift.identifier}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap lineHeight={1.2} display="block">
                        {lift.name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </>
            ),
          } : undefined}
        >
          <TimelineRowGroup>
            {lifts.map((lift) => (
              <TimelineRow
                key={lift.id}
                id={lift.id}
                label={isMobile ? lift.identifier : `${lift.identifier} – ${lift.name}`}
                rowCount={1}
                height={isMobile ? 60 : undefined}
                collapsible={false}
                style={{ borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}` }}
              >
                {(bookingsByLift.get(lift.id) ?? []).map((booking) => (
                  <TimelineItem
                    key={booking.id}
                    startTime={booking.scheduledStart}
                    endTime={booking.scheduledEnd}
                    row={0}
                  >
                    <BookingItem booking={booking} />
                  </TimelineItem>
                ))}
              </TimelineRow>
            ))}
          </TimelineRowGroup>
        </TimelineCalendar>
      </Paper>

      {/*
        Two different silences, told apart (#2500). The condition here was
        `lifts.length === 0` while the message said "no bookings", so a new
        workshop concluded it had no customers — when in fact the calendar
        cannot show a single booking until a lift exists to hang it on.
      */}
      {emptyReason === 'noLifts' && (
        <EmptyState
          icon={<GarageIcon />}
          title={t('calendar.noLifts')}
          description={t('calendar.noLiftsDescription')}
          action={{
            label: t('calendar.addLift'),
            onClick: () => void navigate('/settings?tab=equipment'),
          }}
          minHeight={200}
        />
      )}

      {emptyReason === 'noBookings' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {/* A quiet day is normal and needs no call to action. */}
          <Typography color="text.secondary">{t('calendar.noBookings')}</Typography>
        </Box>
      )}
    </Box>
  );
}
